/** @jsxImportSource @emotion/react */
import React, { useCallback, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import commonStyles from '../../styles/commonStyles';
import {
  Breadcrumb,
  Button,
  Card,
  Checkbox,
  Collapse,
  Empty,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Transfer,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import MainLayout from '../../components/common/MainLayout';
import {
  useGetEventTemplatesQuery,
  useCreateEventTemplateMutation,
  useUpdateEventTemplateMutation,
  useDeleteEventTemplateMutation,
} from '../../api/eventTemplatesApi';
import { useGetPropertiesQuery } from '../../api/plantingEventsApi';
import type {
  EventTemplate,
  EventTemplateCategory,
  EventTemplateProperty,
  CreateEventTemplatePayload,
} from '../../types/eventTemplates';
import type { Property } from '../../types/plantingEvents';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

const { Title, Paragraph, Text } = Typography;

const styles = {
  titleNoMargin: css({ margin: 0 }),
  paragraphNoMargin: css({ margin: 0 }),
  selectWidth220: css({ width: 220 }),
  searchMaxWidth: css({ maxWidth: 320 }),
  templateIcon: css({ fontSize: 20, marginRight: 8 }),
  colorDot: css({ display: 'inline-block', width: 12, height: 12, borderRadius: '50%', marginRight: 6, verticalAlign: 'middle' }),
  templateHeader: css({ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' as const }),
  propertyTable: css({ marginTop: 8 }),
  headerRow: css({ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }),
  templateActions: css({ display: 'flex', gap: 4 }),
};

const CATEGORY_LABEL_KEYS: Record<EventTemplateCategory, string> = {
  inspection: 'eventTemplates.category.inspection',
  measurement: 'eventTemplates.category.measurement',
  treatment: 'eventTemplates.category.treatment',
  care: 'eventTemplates.category.care',
  harvest: 'eventTemplates.category.harvest',
  maintenance: 'eventTemplates.category.maintenance',
  lifecycle: 'eventTemplates.category.lifecycle',
};

const CATEGORY_COLORS: Record<EventTemplateCategory, string> = {
  inspection: 'orange',
  measurement: 'blue',
  treatment: 'red',
  care: 'green',
  harvest: 'gold',
  maintenance: 'cyan',
  lifecycle: 'purple',
};

const DATA_TYPE_LABEL_KEYS: Record<string, string> = {
  text: 'eventTemplates.dataType.text',
  number: 'eventTemplates.dataType.number',
  boolean: 'eventTemplates.dataType.boolean',
  decimal: 'eventTemplates.dataType.decimal',
  integer: 'eventTemplates.dataType.number',
  float: 'eventTemplates.dataType.decimal',
  date: 'eventTemplates.dataType.date',
  datetime: 'eventTemplates.dataType.datetime',
};

const dataTypeLabel = (t: TFunction, dataType: string) => (
  DATA_TYPE_LABEL_KEYS[dataType] ? t(DATA_TYPE_LABEL_KEYS[dataType]) : dataType
);

const propertyColumns = (t: TFunction): ColumnsType<EventTemplateProperty> => [
  {
    title: t('eventTemplates.properties.property'),
    dataIndex: 'name',
    render: (name: string, record) => (
      <Space direction="vertical" size={0}>
        <Text strong>{name}</Text>
        {record.code && <Text type="secondary" style={{ fontSize: 12 }}>{record.code}</Text>}
      </Space>
    ),
  },
  {
    title: t('eventTemplates.properties.dataType'),
    dataIndex: 'dataType',
    width: 120,
    render: (dt: string) => dataTypeLabel(t, dt),
  },
  {
    title: t('eventTemplates.properties.unit'),
    dataIndex: 'unit',
    width: 100,
    render: (unit?: string) => unit || '—',
  },
  {
    title: t('eventTemplates.properties.required'),
    dataIndex: 'isRequired',
    width: 120,
    render: (required: boolean) => required
      ? <Tag color="red">{t('common:actions.yes')}</Tag>
      : <Tag>{t('common:actions.no')}</Tag>,
  },
  {
    title: t('eventTemplates.properties.options'),
    dataIndex: 'options',
    render: (options?: EventTemplateProperty['options']) => {
      if (!options || options.length === 0) return <Text type="secondary">—</Text>;
      return (
        <Space size={[4, 4]} wrap>
          {options.map((opt) => (
            <Tag key={opt.id}>{opt.value}</Tag>
          ))}
        </Space>
      );
    },
  },
];

interface TemplateFormValues {
  code: string;
  name: string;
  description?: string;
  category: EventTemplateCategory;
  assignable: boolean;
  autoComplete: boolean;
  icon?: string;
  targetType?: string;
}

const AdminEventTemplatesPage: React.FC = () => {
  const { t } = useTranslation('admin');
  const [categoryFilter, setCategoryFilter] = useState<EventTemplateCategory | 'all'>('all');
  const [scopeFilter, setScopeFilter] = useState<'all' | 'system' | 'farm'>('all');
  const [search, setSearch] = useState('');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<EventTemplate | null>(null);
  const [selectedPropertyIds, setSelectedPropertyIds] = useState<string[]>([]);
  const [requiredPropertyIds, setRequiredPropertyIds] = useState<Set<number>>(new Set());
  const [form] = Form.useForm<TemplateFormValues>();

  const adminScopeId = useSelector((state: any) => state.saves?.activeSaveId || '') as string;

  const {
    data: templatesResponse,
    isFetching: loading,
    isError,
  } = useGetEventTemplatesQuery({ saveId: adminScopeId }, { skip: !adminScopeId });

  const { data: availableProperties = [] } = useGetPropertiesQuery('planting');

  const [createTemplate, { isLoading: creating }] = useCreateEventTemplateMutation();
  const [updateTemplate, { isLoading: updating }] = useUpdateEventTemplateMutation();
  const [deleteTemplate] = useDeleteEventTemplateMutation();

  const templates = useMemo(() => templatesResponse?.templates ?? [], [templatesResponse]);

  const filtered = useMemo(() => {
    let list = templates;
    if (categoryFilter !== 'all') {
      list = list.filter((t) => t.category === categoryFilter);
    }
    if (scopeFilter !== 'all') {
      list = list.filter((t) => t.scopeType === scopeFilter);
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(
        (t) =>
          t.name.toLowerCase().includes(q) ||
          t.code.toLowerCase().includes(q) ||
          (t.description ?? '').toLowerCase().includes(q),
      );
    }
    return list;
  }, [templates, categoryFilter, scopeFilter, search]);

  const grouped = useMemo(() => {
    const map = new Map<EventTemplateCategory, EventTemplate[]>();
    for (const t of filtered) {
      const cat = t.category;
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat)!.push(t);
    }
    const order: EventTemplateCategory[] = ['lifecycle', 'inspection', 'measurement', 'care', 'treatment', 'harvest', 'maintenance'];
    return order.filter((c) => map.has(c)).map((c) => ({ category: c, templates: map.get(c)! }));
  }, [filtered]);

  // Transfer data source for property selection
  const transferDataSource = useMemo(() =>
    availableProperties.map((p: Property) => ({
      key: String(p.id),
      title: p.name,
      description: `${p.code} — ${dataTypeLabel(t, p.dataType)}${p.unit ? ` (${p.unit})` : ''}`,
    })),
    [availableProperties, t],
  );

  const categoryOptions = Object.entries(CATEGORY_LABEL_KEYS).map(([value, key]) => ({ value, label: t(key) }));

  const openCreateModal = useCallback(() => {
    form.resetFields();
    setSelectedPropertyIds([]);
    setRequiredPropertyIds(new Set());
    setCreateModalOpen(true);
  }, [form]);

  const openEditModal = useCallback((template: EventTemplate) => {
    setEditingTemplate(template);
    form.setFieldsValue({
      code: template.code,
      name: template.name,
      description: template.description,
      category: template.category,
      assignable: template.assignable,
      autoComplete: template.autoComplete,
      icon: template.icon,
      targetType: template.targetType ?? undefined,
    });
    const propIds = (template.properties ?? []).map((p) => String(p.propertyId));
    setSelectedPropertyIds(propIds);
    const reqIds = new Set(
      (template.properties ?? []).filter((p) => p.isRequired).map((p) => p.propertyId),
    );
    setRequiredPropertyIds(reqIds);
  }, [form]);

  const handleCreateSubmit = useCallback(async () => {
    try {
      const values = await form.validateFields();
      if (!adminScopeId) {
        message.error(t('eventTemplates.messages.selectSaveToCreate'));
        return;
      }
      const payload: CreateEventTemplatePayload & { saveId: string } = {
        ...values,
        saveId: adminScopeId,
        propertyIds: selectedPropertyIds.map(Number),
        requiredPropertyIds: Array.from(requiredPropertyIds),
      };
      await createTemplate(payload).unwrap();
      message.success(t('eventTemplates.messages.created'));
      setCreateModalOpen(false);
      form.resetFields();
    } catch (err: any) {
      if (err?.data?.message) message.error(err.data.message);
    }
  }, [form, adminScopeId, selectedPropertyIds, requiredPropertyIds, createTemplate, t]);

  const handleEditSubmit = useCallback(async () => {
    if (!editingTemplate || !adminScopeId) return;
    try {
      const values = await form.validateFields();
      await updateTemplate({
        id: editingTemplate.id,
        saveId: adminScopeId,
        payload: {
          name: values.name,
          description: values.description,
          assignable: values.assignable,
          autoComplete: values.autoComplete,
          icon: values.icon,
        },
      }).unwrap();
      message.success(t('eventTemplates.messages.updated'));
      setEditingTemplate(null);
    } catch (err: any) {
      if (err?.data?.message) message.error(err.data.message);
    }
  }, [editingTemplate, adminScopeId, form, updateTemplate, t]);

  const handleDelete = useCallback((template: EventTemplate) => {
    if (!adminScopeId) return;
    Modal.confirm({
      title: t('eventTemplates.deactivate.title', { name: template.name }),
      content: t('eventTemplates.deactivate.content'),
      okText: t('eventTemplates.deactivate.ok'),
      okType: 'danger',
      cancelText: t('common:actions.cancel'),
      onOk: async () => {
        try {
          await deleteTemplate({ id: template.id, saveId: adminScopeId }).unwrap();
          message.success(t('eventTemplates.messages.deactivated'));
        } catch (err: any) {
          if (err?.data?.message) message.error(err.data.message);
        }
      },
    });
  }, [adminScopeId, deleteTemplate, t]);

  const toggleRequired = useCallback((propertyId: number, checked: boolean) => {
    setRequiredPropertyIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(propertyId);
      else next.delete(propertyId);
      return next;
    });
  }, []);

  const renderTemplateForm = () => (
    <Form form={form} layout="vertical">
      <Form.Item name="code" label={t('eventTemplates.form.code')} rules={[{ required: true, message: t('eventTemplates.form.codeRequired') }]}>
        <Input placeholder="my_custom_template" disabled={!!editingTemplate} />
      </Form.Item>
      <Form.Item name="name" label={t('eventTemplates.form.name')} rules={[{ required: true, message: t('eventTemplates.form.nameRequired') }]}>
        <Input placeholder={t('eventTemplates.form.namePlaceholder')} />
      </Form.Item>
      <Form.Item name="description" label={t('common:labels.description')}>
        <Input.TextArea rows={2} placeholder={t('eventTemplates.form.descriptionPlaceholder')} />
      </Form.Item>
      <Form.Item name="category" label={t('eventTemplates.form.category')} rules={[{ required: true }]}>
        <Select
          options={categoryOptions}
          disabled={!!editingTemplate}
        />
      </Form.Item>
      <Form.Item name="targetType" label={t('eventTemplates.form.targetType')}>
        <Select
          allowClear
          placeholder={t('eventTemplates.target.any')}
          options={[
            { value: 'planting', label: t('eventTemplates.target.planting') },
            { value: 'bed', label: t('eventTemplates.target.bed') },
            { value: 'any', label: t('eventTemplates.target.any') },
          ]}
        />
      </Form.Item>
      <Space>
        <Form.Item name="assignable" valuePropName="checked">
          <Checkbox>{t('eventTemplates.flags.assignable')}</Checkbox>
        </Form.Item>
        <Form.Item name="autoComplete" valuePropName="checked">
          <Checkbox>{t('eventTemplates.flags.autoComplete')}</Checkbox>
        </Form.Item>
      </Space>
      <Form.Item name="icon" label={t('eventTemplates.form.icon')}>
        <Input placeholder="🔍" style={{ width: 80 }} />
      </Form.Item>

      {!editingTemplate && (
        <>
          <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
            {t('eventTemplates.form.properties')}
          </Typography.Text>
          <Transfer
            dataSource={transferDataSource}
            targetKeys={selectedPropertyIds}
            onChange={(nextTargetKeys) => setSelectedPropertyIds(nextTargetKeys as string[])}
            render={(item) => item.title ?? ''}
            titles={[t('eventTemplates.form.available'), t('eventTemplates.form.selected')]}
            showSearch
            filterOption={(input, item) =>
              (item.title ?? '').toLowerCase().includes(input.toLowerCase()) ||
              (item.description ?? '').toLowerCase().includes(input.toLowerCase())
            }
            listStyle={{ width: 280, height: 300 }}
          />
          {selectedPropertyIds.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
                {t('eventTemplates.form.requiredProperties')}
              </Typography.Text>
              {selectedPropertyIds.map((idStr) => {
                const prop = availableProperties.find((p: Property) => String(p.id) === idStr);
                if (!prop) return null;
                return (
                  <div key={idStr} style={{ marginBottom: 2 }}>
                    <Checkbox
                      checked={requiredPropertyIds.has(prop.id)}
                      onChange={(e) => toggleRequired(prop.id, e.target.checked)}
                    >
                      {prop.name}
                    </Checkbox>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </Form>
  );

  return (
    <MainLayout
      breadcrumb={
        <Breadcrumb
          items={[
            { title: t('eventTemplates.breadcrumb.admin') },
            { title: t('eventTemplates.title') },
          ]}
        />
      }
    >
      <Space direction="vertical" size={16} css={commonStyles.fullWidth}>
        <Card>
          <div css={styles.headerRow}>
            <Space direction="vertical" size={4}>
              <Title level={4} css={styles.titleNoMargin}>
                {t('eventTemplates.title')}
              </Title>
              <Paragraph type="secondary" css={styles.paragraphNoMargin}>
                {t('eventTemplates.intro')}
              </Paragraph>
            </Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={openCreateModal}
              disabled={!adminScopeId}
              title={!adminScopeId ? t('eventTemplates.selectSave') : undefined}
            >
              {t('eventTemplates.create.button')}
            </Button>
          </div>
        </Card>

        <Card>
          <Space css={commonStyles.fullWidth} align="center" wrap>
            <Select
              value={categoryFilter}
              onChange={setCategoryFilter}
              css={styles.selectWidth220}
              options={[
                { value: 'all', label: t('eventTemplates.filters.allCategories') },
                ...categoryOptions,
              ]}
            />
            <Select
              value={scopeFilter}
              onChange={setScopeFilter}
              css={styles.selectWidth220}
              options={[
                { value: 'all', label: t('eventTemplates.filters.allScopes') },
                { value: 'system', label: t('eventTemplates.filters.system') },
                { value: 'farm', label: t('eventTemplates.filters.farm') },
              ]}
            />
            <Input.Search
              placeholder={t('eventTemplates.filters.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              allowClear
              css={styles.searchMaxWidth}
            />
          </Space>
        </Card>

        {loading && <Spin size="large" />}
        {isError && <Card><Empty description={t('eventTemplates.loadFailed')} /></Card>}

        {!loading && !isError && grouped.length === 0 && (
          <Card><Empty description={t('eventTemplates.empty')} /></Card>
        )}

        {grouped.map(({ category, templates: catTemplates }) => (
          <Card key={category} title={
            <Space>
              <Tag color={CATEGORY_COLORS[category]}>{t(CATEGORY_LABEL_KEYS[category])}</Tag>
              <Text type="secondary">{t('eventTemplates.templateCount', { count: catTemplates.length })}</Text>
            </Space>
          }>
            <Collapse
              items={catTemplates.map((template) => ({
                key: template.id,
                label: (
                  <div css={styles.templateHeader}>
                    {template.icon && <span css={styles.templateIcon}>{template.icon}</span>}
                    {template.color && <span css={styles.colorDot} style={{ backgroundColor: template.color }} />}
                    <Text strong>{template.name}</Text>
                    <Tag color={template.scopeType === 'system' ? 'purple' : 'green'}>
                      {template.scopeType === 'system' ? t('eventTemplates.scope.system') : t('eventTemplates.scope.farm')}
                    </Tag>
                    <Text type="secondary" style={{ fontSize: 12 }}>({template.code})</Text>
                    {!template.isActive && <Tag color="red">{t('eventTemplates.inactive')}</Tag>}
                    {template.scopeType === 'farm' && (
                      <span css={styles.templateActions} onClick={(e) => e.stopPropagation()}>
                        <Button
                          size="small"
                          type="text"
                          icon={<EditOutlined />}
                          onClick={() => openEditModal(template)}
                        />
                        <Button
                          size="small"
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => handleDelete(template)}
                        />
                      </span>
                    )}
                  </div>
                ),
                children: (
                  <Space direction="vertical" size={8} css={commonStyles.fullWidth}>
                    {template.description && (
                      <Paragraph type="secondary" css={styles.paragraphNoMargin}>
                        {template.description}
                      </Paragraph>
                    )}
                    <Space wrap>
                      {template.assignable && <Tag color="blue">{t('eventTemplates.flags.assignable')}</Tag>}
                      {template.autoComplete && <Tag color="green">{t('eventTemplates.flags.autoComplete')}</Tag>}
                      {template.targetType && <Tag>{t('eventTemplates.targetTag', { target: template.targetType })}</Tag>}
                    </Space>
                    {template.properties && template.properties.length > 0 ? (
                      <Table
                        css={styles.propertyTable}
                        columns={propertyColumns(t)}
                        dataSource={template.properties}
                        rowKey="propertyId"
                        size="small"
                        pagination={false}
                      />
                    ) : (
                      <Text type="secondary">{t('eventTemplates.noProperties')}</Text>
                    )}
                  </Space>
                ),
              }))}
            />
          </Card>
        ))}
      </Space>

      {/* Create Template Modal */}
      <Modal
        title={t('eventTemplates.create.title')}
        open={createModalOpen}
        onCancel={() => setCreateModalOpen(false)}
        onOk={handleCreateSubmit}
        confirmLoading={creating}
        okText={t('common:actions.create')}
        cancelText={t('common:actions.cancel')}
        width={700}
        destroyOnClose
      >
        {renderTemplateForm()}
      </Modal>

      {/* Edit Template Modal */}
      <Modal
        title={t('eventTemplates.edit.title', { name: editingTemplate?.name ?? '' })}
        open={!!editingTemplate}
        onCancel={() => setEditingTemplate(null)}
        onOk={handleEditSubmit}
        confirmLoading={updating}
        okText={t('common:actions.save')}
        cancelText={t('common:actions.cancel')}
        width={700}
        destroyOnClose
      >
        {renderTemplateForm()}
      </Modal>
    </MainLayout>
  );
};

export default AdminEventTemplatesPage;
